import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Send, Sparkles, Calculator, ArrowRight } from "lucide-react";
import { AMC, waLink } from "@/data/site";
import { WaIcon, ease, useLang } from "./common";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const renderMd = (txt) =>
  txt.split("\n").map((line, i) => {
    const html = line
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");
    return <span key={i}>{html ? <span dangerouslySetInnerHTML={{ __html: html }} /> : "\u00A0"}<br /></span>;
  });

export const streamLLM = (path, body, onDelta, onDone) => {
  fetch(`${BACKEND}/api/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then(async (res) => {
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        if (chunk.startsWith("data: ")) {
          try {
            const d = JSON.parse(chunk.slice(6));
            if (d.delta) onDelta(d.delta);
            if (d.error) onDelta(`⚠ ${d.error}`);
            if (d.done) onDone();
          } catch { /* partial */ }
        }
      }
    }
    onDone();
  }).catch(() => onDelta("\n⚠ Connection issue — please try again."));
};

const getSessionId = () => {
  let id = sessionStorage.getItem("ve_session");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("ve_session", id);
  }
  return id;
};

const Dot = ({ d }) => <span className="w-1.5 h-1.5 rounded-full bg-[#00A651] animate-bounce inline-block" style={{ animationDelay: `${d}s` }} />;

const AiPanel = ({ showEstimator }) => {
  const { t } = useLang();
  const [msgs, setMsgs] = useState([{ role: "assistant", text: t.widget.welcome }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sid = useMemo(getSessionId, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const send = (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "", streaming: true }]);
    streamLLM(
      "ai/chat",
      { session_id: sid, message: q },
      (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + d, streaming: true }; return c; }),
      () => { setMsgs((m) => { const c = [...m]; c[c.length - 1].streaming = false; return c; }); setBusy(false); }
    );
  };

  return (
    <div className="p-4 space-y-3 bg-[#F8F9FA]">
      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1" data-testid="ai-messages">
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            <div data-testid={i === msgs.length - 1 && m.streaming ? "ai-message-stream" : undefined}
              className={`text-sm leading-relaxed rounded-2xl px-3.5 py-2.5 shadow-sm max-w-[92%] ${m.role === "user" ? "bg-[#0F295B] text-white rounded-br-sm" : "bg-white text-[#0F295B] rounded-tl-sm"}`}>
              {m.role === "assistant" && !m.text && m.streaming ? (
                <span className="flex gap-1 py-1"><Dot d="0" /><Dot d="0.15" /><Dot d="0.3" /></span>
              ) : renderMd(m.text)}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      {msgs.length === 1 && (
        <div className="flex flex-wrap gap-1.5">
          {t.widget.suggestions.map((s, i) => (
            <button key={s} data-testid={`ai-suggest-${i}`} onClick={() => send(s)}
              className="text-xs font-semibold text-[#0F295B] bg-white border border-[#0F295B]/15 rounded-full px-3 py-1.5 hover:border-[#00A651] hover:text-[#00A651] transition-colors">{s}</button>
          ))}
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-input" placeholder={t.widget.ph}
          className="flex-1 rounded-full bg-white border border-[#0F295B]/15 px-4 py-2.5 text-sm text-[#0F295B] placeholder:text-[#6B7A93] focus:outline-none focus:border-[#00A651]" />
        <button type="submit" disabled={busy} data-testid="ai-send" aria-label="Send"
          className="w-10 h-10 shrink-0 rounded-full bg-[#00A651] text-white grid place-items-center disabled:opacity-50 hover:bg-[#008842] transition-colors">
          <Send className="w-4 h-4" />
        </button>
      </form>
      <button onClick={showEstimator} data-testid="est-open" className="w-full flex items-center justify-center gap-2 text-xs font-bold text-[#0F295B] bg-[#F39C12]/15 border border-[#F39C12]/40 rounded-full py-2.5 hover:bg-[#F39C12]/25 transition-colors">
        <Calculator className="w-4 h-4 text-[#A86200]" /> {t.widget.estOpen}
      </button>
    </div>
  );
};

const Estimator = ({ onBack }) => {
  const { t } = useLang();
  const [f, setF] = useState({ bill: "", area: "", floor: "", hasSolar: "no", notes: "" });
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useMemo(getSessionId, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setResult("");
    streamLLM(
      "ai/estimate",
      { session_id: sid, monthly_bill: Number(f.bill) || 0, roof_area: f.area, floor: f.floor, existing_solar: f.hasSolar, notes: f.notes, lang: t.otherLang === "English" ? "hi" : "en" },
      (d) => setResult((r) => r + d),
      () => setBusy(false)
    );
  };

  const inp = "w-full rounded-2xl bg-white border border-[#0F295B]/15 px-3.5 py-2.5 text-sm text-[#0F295B] placeholder:text-[#6B7A93] focus:outline-none focus:border-[#00A651]";
  return (
    <div className="p-4 bg-[#F8F9FA] space-y-3 max-h-[440px] overflow-y-auto" data-testid="estimator-panel">
      <button onClick={onBack} data-testid="est-back" className="text-xs font-bold text-[#4A5B78] flex items-center gap-1 hover:text-[#0F295B]"><ArrowRight className="w-3.5 h-3.5 rotate-180" /> {t.widget.estBack}</button>
      {!result && !busy && (
        <form onSubmit={submit} className="space-y-3" data-testid="estimator-form">
          <p className="text-sm font-semibold text-[#0F295B]">{t.widget.estTitle}</p>
          <label className="block text-xs font-bold text-[#0F295B]">{t.widget.estBill}
            <input required type="number" min="500" max="100000" value={f.bill} onChange={set("bill")} data-testid="est-bill" placeholder={t.widget.phBill} className={`${inp} mt-1`} /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs font-bold text-[#0F295B]">{t.widget.estArea}
              <input value={f.area} onChange={set("area")} data-testid="est-area" placeholder={t.widget.phArea} className={`${inp} mt-1`} /></label>
            <label className="block text-xs font-bold text-[#0F295B]">{t.widget.estFloor}
              <input value={f.floor} onChange={set("floor")} data-testid="est-floor" placeholder={t.widget.phFloor} className={`${inp} mt-1`} /></label>
          </div>
          <label className="block text-xs font-bold text-[#0F295B]">{t.widget.estHas}
            <select value={f.hasSolar} onChange={set("hasSolar")} data-testid="est-has-solar" className={`${inp} mt-1`}>
              <option value="no">{t.widget.estNo}</option><option value="yes">{t.widget.estYes}</option>
            </select></label>
          <button type="submit" data-testid="est-submit" className="w-full rounded-full bg-[#F39C12] text-[#0F295B] font-bold py-3 text-sm hover:bg-[#ffb13b] transition-colors flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4" /> {t.widget.estSubmit}
          </button>
        </form>
      )}
      {busy && <p className="text-sm text-[#4A5B78] flex items-center gap-2"><Dot d="0" /><Dot d="0.15" /><Dot d="0.3" /> {t.widget.estBusy}</p>}
      {result && (
        <div data-testid="est-result" className="text-sm leading-relaxed text-[#0F295B] bg-white rounded-2xl p-4 shadow-sm">
          {renderMd(result)}
          <a href={waLink(t.widget.estWaMsg)} target="_blank" rel="noreferrer" data-testid="est-whatsapp"
            className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#00A651] text-white font-bold py-3 text-sm hover:bg-[#008842] transition-colors">
            <WaIcon className="w-4 h-4" /> {t.widget.estWa}
          </a>
        </div>
      )}
    </div>
  );
};

export const WhatsAppWidget = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ai");
  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div data-testid="wa-widget-panel" initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.95 }} transition={{ duration: 0.35, ease }}
            className="w-[min(380px,calc(100vw-2.5rem))] rounded-3xl overflow-hidden bg-white shadow-[0_30px_60px_-20px_rgba(15,41,91,0.5)] border border-[#0F295B]/10 origin-bottom-right">
            <div className="bg-[#00A651] text-white px-5 py-4 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-white grid place-items-center"><img src="/favicon.svg" alt="" className="w-6 h-6" /></span>
              <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-white/95">{t.widget.powered}</p>
            </div>
            <div className="flex border-b border-[#0F295B]/10 bg-white">
              <button onClick={() => setTab("ai")} data-testid="wa-tab-ai" className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold ${tab !== "wa" ? "text-[#00A651] border-b-2 border-[#00A651]" : "text-[#4A5B78]"}`}>
                <Sparkles className="w-3.5 h-3.5" /> {t.widget.tabs[0]}
              </button>
              <button onClick={() => setTab("wa")} data-testid="wa-tab-whatsapp" className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold ${tab === "wa" ? "text-[#00A651] border-b-2 border-[#00A651]" : "text-[#4A5B78]"}`}>
                <WaIcon className="w-3.5 h-3.5" /> {t.widget.tabs[1]}
              </button>
            </div>
            {tab === "ai" ? (
              <AiPanel key={t.otherLang} showEstimator={() => setTab("est")} />
            ) : tab === "est" ? (
              <Estimator key={t.otherLang} onBack={() => setTab("ai")} />
            ) : (
              <div className="p-4 space-y-2 bg-[#F8F9FA]">
                <p className="text-sm text-[#0F295B] bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm">Namaste! {t.hero.cta}?</p>
                {t.widget.quick.map((q, i) => (
                  <a key={q} href={waLink(`Hi Vivan Energia, ${q}`)} target="_blank" rel="noreferrer" data-testid={`wa-quick-${i}`}
                    className="flex items-center justify-between gap-2 text-sm font-semibold text-[#0F295B] bg-white border border-[#0F295B]/10 rounded-2xl px-4 py-3 hover:border-[#00A651] hover:text-[#00A651] transition-colors">
                    {q}<ArrowUpRight className="w-4 h-4 shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }} onClick={() => setOpen((o) => !o)} data-testid="wa-widget-toggle" aria-label="Chat with us"
        className="wa-pulse relative w-16 h-16 rounded-full bg-[#00A651] text-white grid place-items-center shadow-[0_15px_35px_-10px_rgba(0,166,81,0.9)]">
        {open ? <X className="w-6 h-6" /> : <WaIcon className="w-8 h-8" />}
      </motion.button>
    </div>
  );
};
